import * as React from "react";
import { Layout, Dropdown, Menu, Icon, Avatar, Row, Col, Checkbox, Divider, Button } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import "antd/lib/layout/style";
import "antd/lib/dropdown/style";
import "antd/lib/menu/style";
import "antd/lib/icon/style";
import "antd/lib/avatar/style";
import "antd/lib/row/style";
import "antd/lib/col/style";
import "antd/lib/checkbox/style";
import "antd/lib/divider/style";
import "antd/lib/button/style";
import VhIconCommon from "vh-icon-common";
import logo from "./logo.png";

import "./MsHeader.scss";

/**
 * 应用类型
 *
 * @export
 * @interface MsApp
 */
export interface MsApp {
  /**
   * appId
   *
   * @type {string}
   * @memberof MsApp
   */
  appId: string;
  /**
   * appCode
   *
   * @type {string}
   * @memberof MsApp
   */
  appCode: string;
  /**
   * 应用名称
   *
   * @type {string}
   * @memberof MsApp
   */
  appName: string;
  /**
   * 应用类型区分
   * 4-平台应用 其他-非平台应用
   *
   * @type {string}
   * @memberof MsApp
   */
  appTypeId?: number;
  /**
   * 应用链接地址
   *
   * @type {string}
   * @memberof MsApp
   */
  appUrl?: string;
  /**
   * 应用文字图标
   *
   * @type {string}
   * @memberof MsApp
   */
  appFontName?: string;
  /**
   * 文字图标颜色
   *
   * @type {string}
   * @memberof MsApp
   */
  appFontColor?: string;
  /**
   * 显示方式 1：内嵌 2：外链
   *
   * @type {("1" | "2")}
   * @memberof MsApp
   */
  displayMode?: "1" | "2";
  /**
   * 是否常用 0：非常用 1：常用
   *
   * @type {("0" | "1")}
   * @memberof MsApp
   */
  settingFlag?: "0" | "1";
  /**
   * 分组 1：右上角 2：其他
   *
   * @type {("1" | "2")}
   * @memberof MsApp
   */
  groupId?: "1" | "2";
}

/**
 * 头部属性声明
 *
 * @interface MsHeaderProps
 */
export interface MsHeaderProps {
  /**
   * 点击logo主页跳转地址
   *
   * @type {string}
   * @memberof MsHeaderProps
   */
  homePage?: string;

  /**
   * 头部高度
   *
   * @type {number}
   * @memberof MsHeaderProps
   */
  height?: number;

  /**
   * 用户照片
   *
   * @type {string}
   * @memberof MsHeaderProps
   */
  picture?: string;

  /**
   * 用户名称
   *
   * @type {string}
   * @memberof MsHeaderProps
   */
  name?: string;

  /**
   * 当前登录用户应用列表
   *
   * @type {MsApp[]}
   * @memberof MsHeaderProps
   */
  appList?: MsApp[];

  /**
   * groupId为2的app列表，从home-server获取
   * 临时使用，以后应当删除
   *
   * @type {MsApp[]}
   * @memberof MsHeaderProps
   */
  tempAppList?: MsApp[];

  /**
   * 当前所处应用code
   *
   * @type {string}
   * @memberof MsHeaderProps
   */
  selectedApp?: string;

  /**
   * 退出
   *
   * @memberof MsHeaderProps
   */
  onLogout?: () => void;

  /**
   * 保存常用应用
   *
   * @memberof MsHeaderProps
   */
  onSave?: (idList: string[]) => void;
}

/**
 * 头部state声明
 *
 * @interface MsHeaderState
 */
interface MsHeaderState {
  // 展开更多
  moreVisible: boolean;
  // 设置常用模块展开
  settingVisible: boolean;
  // 常用应用列表
  selectedAppList: string[];
}

/**
 * 头部设置
 * 1、高度设置
 * @class MsHeader
 * @extends {React.Component}
 */
class MsHeader extends React.Component<MsHeaderProps, MsHeaderState> {
  /**
   * Creates an instance of MsHeader.
   * @param {MsHeaderProps} props
   * @memberof MsHeader
   */
  constructor(props: MsHeaderProps) {
    super(props);

    // 初始化state
    this.state = {
      moreVisible: false,
      settingVisible: false,
      selectedAppList: []
    }
  }

  /**
   * 显示更多应用
   *
   * @memberof MsHeader
   */
  public onShowMore = (appCode: string) => {
    if (appCode === "more") {
      // 常用应用设置
      const selectedAppList = this.props.appList ? this.props.appList.filter((app: MsApp) => app.appTypeId !== 4 && app.settingFlag === "1").map((app: MsApp) => app.appId) : [];
      this.setState({
        moreVisible: !this.state.moreVisible,
        selectedAppList
      });
    }
  }

  /**
   * 点击应用
   *
   * @memberof MsHeader
   */
  public onAppClick = (app: MsApp) => {
    this.onClose();
    window.open(app.appUrl || "/", app.displayMode === "2" ? "_blank" : "_self");
  }

  /**
   * 显示常用模块设置
   *
   * @memberof MsHeader
   */
  public onShowSetting = () => {
    this.setState({
      settingVisible: !this.state.settingVisible
    });
  }

  /**
   * 选择常用应用
   *
   * @memberof MsHeader
   */
  public onChange = (e: CheckboxChangeEvent) => {
    // 选中的场合
    if (e.target.checked) {
      this.setState({
        selectedAppList: [...this.state.selectedAppList, e.target.value]
      });
    } else {
      this.setState({
        selectedAppList: this.state.selectedAppList.filter((appId: string) => appId !== e.target.value)
      });
    }
  }

  /**
   * 选择常用应用
   *
   * @memberof MsHeader
   */
  public onSave = () => {
    if (this.props.onSave) {
      this.props.onSave(this.state.selectedAppList);
      this.onClose();
    }
  }

  /**
   * 关闭更多应用
   *
   * @memberof MsHeader
   */
  public onClose = () => {
    this.setState({
      moreVisible: false,
      settingVisible: false
    });
  }

  /**
   * 登出
   *
   * @memberof MsHeader
   */
  public onLogout = () => {
    if (this.props.onLogout) {
      this.props.onLogout();
    }
  }

  /**
   * 渲染
   *
   * @returns
   * @memberof MsHeader
   */
  public render() {
    const { appList } = this.props;
    const { selectedAppList } = this.state;

    // 右边应用（个人中心，公司设置等）应用列表
    const configList = [];
    // 平台应用列表
    const platformAppList: MsApp[] = [];
    // 非平台应用列表
    const notPlatformAppList: MsApp[] = [];
    // 常用应用列表
    const usuallyAppList: MsApp[] = [];
    // 非常用应用列表
    const notUsuallyAppList: MsApp[] = [];

    // 头部展示应用列表，初始化为平台应用(appTypeId:4)，后面会再添加最多三个常用应用
    let headerAppList: MsApp[] = [{
      appId: "studio",
      appCode: "studio",
      appName: "工作台",
      appUrl: this.props.homePage || "/",
      displayMode: "1"
    }];

    // 应用列表存在的场合
    if (appList && appList.length) {
      appList.forEach((app: MsApp) => {
        if (app.groupId === "2") {
          if (app.appTypeId === 4) {
            // 平台应用
            platformAppList.push(app);
          } else {
            // 非平台应用
            notPlatformAppList.push(app);

            if (app.settingFlag === "0") {
              // 非常用应用列表
              notUsuallyAppList.push(app);
            } else if (app.settingFlag === "1") {
              // 常用应用列表
              usuallyAppList.push(app);
            }
          }
        } else if (app.groupId === "1") {
          // 右上角用户应用（个人中心，公司设置等）应用列表
          configList.push(app);
        }
      })
    }

    // 非平台应用3个以下的场合，直接全部显示在头部，并且不显示【更多应用】
    if (notPlatformAppList.length <= 3) {
      // 头部显示应用为【平台应用】加上【非平台应用】
      headerAppList = [...headerAppList, ...platformAppList, ...notPlatformAppList];
    } else {
      // 头部显示应用为【平台应用】加上【常用应用】
      headerAppList = [...headerAppList, ...platformAppList, ...usuallyAppList];

      // 不常用应用存在的场合，添加【更多应用】
      if (notUsuallyAppList.length) {
        headerAppList.push({
          appId: "more",
          appName: "更多应用",
          appCode: "more"
        });
      }
    }

    // 不在头部列表的场合,则activeClass设置在更多应用上
    let isMore = false;
    if (!headerAppList.find((app: MsApp) => app.appCode === this.props.selectedApp)) {
      isMore = true;
    }

    return (
      <Layout.Header className="ms-header" style={ this.props.height ? { height: this.props.height, backgroundColor: "#fff" } : { backgroundColor: "#fff" } }>
        <a href={ this.props.homePage }>
          <img src={ logo } className="logo" />
        </a>
        <ul className="ms-nav">
          {
            headerAppList.map((app: MsApp) => (
              <li key={ app.appCode } className={ (isMore && app.appCode === "more") || this.props.selectedApp === app.appCode ? "selected-nav" : "" }>
                <a href={ app.appCode === "more" ? undefined : (app.appUrl || "/") } target={ app.displayMode === "2" ? "_blank" : "_self" } onClick={ this.onShowMore.bind(this, app.appCode) }>{ app.appName }</a>
                <div className={ "selected-nav-border" } />
                {
                  // 更多应用
                  this.state.moreVisible && app.appCode === "more" ?
                  <div>
                    <div className="ant-modal-mask" style={ {top: 64, cursor: "pointer"} } onClick={ this.onClose } />
                    <div className="more-app-container">
                      {/* 三角形 */}
                      <div className="triangle" />

                      {/* 非常用列表 */}
                      <Row className="more-app-body">
                        {
                          notUsuallyAppList.map((app: MsApp)=>
                            <Col span={ 5 } style={ {width: "20%"} } key={ app.appCode } onClick={ this.onAppClick.bind(this, app) }>
                              {/* <Avatar size={ 50 }> */}
                                <VhIconCommon style={{fontSize: 40}} type={ app.appFontName || "ms-more-app" } />
                              {/* </Avatar> */}
                              <div className="app-name">{ app.appName }</div>
                            </Col>
                          )
                        }
                      </Row>

                      {/* 常用应用设置链接 */}
                      <Row className="setting-link">
                        <span onClick={ this.onShowSetting }>设置导航栏常用模块<Icon type={ this.state.settingVisible ? "caret-down" : "caret-right" } /></span>
                      </Row>

                      {/* 常用应用设置 */}
                      {
                        this.state.settingVisible ?
                        <Row className="setting-app-container">
                          {/* 分割线 */}
                          <Divider />

                          {/* 标题 */}
                          <Row className="setting-app-title">设置导航栏常用模块（最多可设置3个）</Row>

                          {/* 非平台应用列表-常用应用设置 */}
                          <Row className="setting-app-body">
                            {
                              notPlatformAppList.map((app: MsApp) => (
                                <Col span={ 6 } key={ app.appCode }>
                                  <Checkbox checked={ selectedAppList.includes(app.appId) } disabled={ selectedAppList.length > 2 && !selectedAppList.includes(app.appId) } value={ app.appId } onChange={ this.onChange }>{ app.appName }</Checkbox>
                                </Col>
                              ))
                            }
                          </Row>

                          {/* 保存按钮 */}
                          <Row className="setting-app-body" style={ {paddingTop: 16} }>
                            <Button type="primary" shape="round" disabled={ notPlatformAppList.length === 0 } onClick={ this.onSave }>确定</Button>
                          </Row>
                        </Row> : null
                      }
                    </div>
                  </div> : null
                }
              </li>
            ))
          }
        </ul>

        <Dropdown overlay={
          <Menu>
            <Menu.Item key="2">
              <a style={ {paddingLeft: 20, paddingRight: 20} } href={ `${ process.env.REACT_APP_ENV === "pro" ? "https" : "http" }://${ process.env.REACT_APP_ENV !== "pro" ? process.env.REACT_APP_ENV : "" }config.viphrm.com/personal` }>
                <VhIconCommon type="ms-personal" style={ {marginRight: 8} } />个人中心
              </a>
            </Menu.Item>
            {
              (this.props.tempAppList || []).filter((app: MsApp) => app.appCode !== "15001").map((app: MsApp) => (
                <Menu.Item key={ app.appCode }>
                  <a style={ {paddingLeft: 20, paddingRight: 20} } href={ app.appUrl }>
                    <VhIconCommon type={ app.appFontName && app.appFontName.startsWith("ms-") ? app.appFontName : "ms-more-app" } style={ {marginRight: 8} } />{ app.appName }
                  </a>
                </Menu.Item>
              ))
            }
            <Menu.Item key="1">
              <a style={ {paddingLeft: 20, paddingRight: 20} } href="javascript:;" onClick={ this.onLogout }>
                <VhIconCommon type="ms-quit" style={ {marginRight: 8} } />退出
              </a>
            </Menu.Item>
          </Menu>
        } trigger={ ["click"] }>
          <div className="user-info">
            { this.props.name ? (this.props.name.length > 20 ? (this.props.name.substr(0, 19) + "...") : this.props.name) : "" }
            <Avatar size={ 30 } src={ this.props.picture } icon="user" style={ {margin: "0 7px"} } />
            <Icon type="down" />
          </div>
        </Dropdown>
      </Layout.Header>
    )
  }
}

export default MsHeader;
